import AddCircleIcon from '@mui/icons-material/AddCircle';
import SearchIcon from '@mui/icons-material/Search';
import {
    Grid,
    Stack,
    InputAdornment,
    MenuItem,
    Select,
    SelectChangeEvent,
    TextField,
    Typography,
    useMediaQuery,
} from '@mui/material';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CustomButton } from '../../../layout/styledcomponents/CustomButton';
import { CustomIconButton } from '../../../layout/styledcomponents/CustomIconButton';

export type CapSortKey = 'nameAsc' | 'nameDesc' | 'capHigh' | 'capLow';

const EXPERIENCE_TYPE_OPTIONS = [
    'All',
    'boat',
    'vehicle',
    'activity',
    'commercial',
    'property',
] as const;

export default function BookingCapToolbar({
    query,
    onQueryChange,
    experienceType,
    onExperienceTypeChange,
    appliesTo,
    onAppliesToChange,
    appliesToOptions,
    sortKey,
    onSortKeyChange,
}: {
    query: string;
    onQueryChange: (v: string) => void;
    experienceType: string; // 'All' | one of the experience types
    onExperienceTypeChange: (v: string) => void;
    appliesTo: string; // 'All' | 'user' | 'company'
    onAppliesToChange: (v: string) => void;
    appliesToOptions: string[]; // e.g. ['All','user','company']
    sortKey: CapSortKey;
    onSortKeyChange: (v: CapSortKey) => void;
}) {
    const navigate = useNavigate();
    const isMobile = useMediaQuery('(max-width: 770px)');
    const isIpad = useMediaQuery('(max-width: 1200px)');

    const handleAddClick = () => {
        navigate(`/newBookingCap`);
    };

    return (
        <Stack sx={{ py: isMobile || isIpad ? 1 : 2, width: '100%' }}>
            {/* Top row: helper text + add button */}
            <Grid container spacing={1.5} alignItems='center'>
                <Grid item xs={12} md>
                    <Typography
                        sx={{
                            fontSize: '12px',
                            whiteSpace: 'nowrap',
                            pr: { md: 0.5 },
                        }}
                    >
                        Set limits per user or organisation within a chosen
                        timeframe.
                    </Typography>
                </Grid>

                <Grid
                    item
                    xs={12}
                    md='auto'
                    sx={{ textAlign: { xs: 'left', md: 'right' } }}
                >
                    {isMobile || isIpad ? (
                        <CustomIconButton
                            variant='contained'
                            color='primary'
                            onClick={handleAddClick}
                        >
                            <AddCircleIcon />
                        </CustomIconButton>
                    ) : (
                        <CustomButton
                            id='addBookingCap'
                            variant='contained'
                            color='primary'
                            startIcon={<AddCircleIcon />}
                            onClick={handleAddClick}
                        >
                            Add New
                        </CustomButton>
                    )}
                </Grid>
            </Grid>

            {/* Filters row */}
            <Grid
                container
                spacing={1.5}
                alignItems='center'
                sx={{
                    pt: 3,
                    flexWrap: { xs: 'wrap', md: 'nowrap' }, // single line on md+
                }}
            >
                {/* Search */}
                <Grid item xs={12} md sx={{ minWidth: { md: 260 } }}>
                    <TextField
                        placeholder='Search booking caps…'
                        size='small'
                        fullWidth
                        value={query}
                        onChange={(e) => onQueryChange(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position='start'>
                                    <SearchIcon fontSize='small' />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>

                {/* Experience Type */}
                <Grid item xs={12} md='auto' sx={{ width: { md: 180 } }}>
                    <Select
                        size='small'
                        fullWidth
                        value={experienceType}
                        onChange={(e: SelectChangeEvent<string>) =>
                            onExperienceTypeChange(e.target.value)
                        }
                    >
                        {EXPERIENCE_TYPE_OPTIONS.map((opt) => (
                            <MenuItem key={opt} value={opt}>
                                {opt === 'All'
                                    ? 'All Experiences'
                                    : opt.charAt(0).toUpperCase() +
                                      opt.slice(1)}
                            </MenuItem>
                        ))}
                    </Select>
                </Grid>

                {/* Applies To */}
                <Grid item xs={12} md='auto' sx={{ width: { md: 180 } }}>
                    <Select
                        size='small'
                        fullWidth
                        value={appliesTo}
                        onChange={(e: SelectChangeEvent<string>) =>
                            onAppliesToChange(e.target.value)
                        }
                    >
                        {appliesToOptions.map((opt) => (
                            <MenuItem key={opt} value={opt}>
                                {opt === 'All'
                                    ? 'Applies To: All'
                                    : opt.charAt(0).toUpperCase() +
                                      opt.slice(1)}
                            </MenuItem>
                        ))}
                    </Select>
                </Grid>

                {/* Sort */}
                <Grid item xs={12} md='auto' sx={{ width: { md: 170 } }}>
                    <Select
                        size='small'
                        fullWidth
                        value={sortKey}
                        onChange={(e: SelectChangeEvent<CapSortKey>) =>
                            onSortKeyChange(e.target.value as CapSortKey)
                        }
                    >
                        <MenuItem value='nameAsc'>Sort: Name ↑</MenuItem>
                        <MenuItem value='nameDesc'>Sort: Name ↓</MenuItem>
                        <MenuItem value='capHigh'>Sort: Cap High</MenuItem>
                        <MenuItem value='capLow'>Sort: Cap Low</MenuItem>
                    </Select>
                </Grid>
            </Grid>
        </Stack>
    );
}
